import { useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase/config';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';

const SANDBOX_ORIGIN    = 'https://applab.lagbasn.se';
const ALLOWED_BLOCKS    = ['ai.chat', 'ai.extract', 'qr.generate', 'chart.render', 'wordcloud.generate'];
const META_BLOCKS       = ['_meta.getCurrentUser', '_meta.getQuota', '_meta.listBlocks'];
const MAX_PER_SEC       = 10;
const MAX_PER_MIN       = 60;
const QUOTA_WARN_PCT    = 80;

export function useApplabBridge({ iframeRef, active }) {
  const { currentUser } = useAuth();
  const callLog = useRef([]); // timestamps för rate-limiting

  useEffect(() => {
    if (!active || !currentUser) return;

    function sendToIframe(payload) {
      iframeRef.current?.contentWindow?.postMessage(payload, SANDBOX_ORIGIN);
    }

    function reply(requestId, result, error) {
      sendToIframe({
        type: 'applab-response',
        requestId,
        result: result ?? null,
        error:  error  ?? null,
      });
    }

    function sendEvent(event, data) {
      sendToIframe({ type: 'applab-event', event, data });
    }

    async function handleMessage(event) {
      // Logga misstänkt trafik riktad till oss men från fel origin
      if (event.origin !== SANDBOX_ORIGIN) {
        if ((event.data || {}).type === 'applab-request') {
          addDoc(collection(db, 'applab_bridge_errors'), {
            origin:    event.origin,
            block:     event.data?.block || null,
            timestamp: serverTimestamp(),
          }).catch(() => {});
        }
        return;
      }

      const { type, requestId, block, params } = event.data || {};
      if (type !== 'applab-request' || !requestId || !block) return;

      // ── Rate-limiting ────────────────────────────────────────────────────────
      const now = Date.now();
      callLog.current = callLog.current.filter(t => now - t < 60000);

      const lastSec = callLog.current.filter(t => now - t < 1000).length;
      if (lastSec >= MAX_PER_SEC) {
        return reply(requestId, null, { code: 'RATE_LIMITED', message: 'För många anrop per sekund (max 10/sek). Vänta lite.' });
      }
      if (callLog.current.length >= MAX_PER_MIN) {
        return reply(requestId, null, { code: 'RATE_LIMITED', message: 'För många anrop per minut (max 60/min). Vänta lite.' });
      }
      callLog.current.push(now);

      // ── Meta-block (löses lokalt, räknas inte mot kvot) ──────────────────────
      if (META_BLOCKS.includes(block)) {
        if (block === '_meta.getCurrentUser') {
          return reply(requestId, {
            displayName: currentUser.displayName || '',
            email:       currentUser.email || '',
            photoURL:    currentUser.photoURL || null,
          });
        }

        if (block === '_meta.getQuota') {
          try {
            const snap  = await getDoc(doc(db, 'applab_quotas', currentUser.uid));
            const d     = snap.data() || {};
            const month = new Date().toISOString().slice(0, 7);
            const used  = d.month === month ? (d.tokens_used_this_month || 0) : 0;
            const limit = d.monthly_limit || 1_000_000;
            const remaining = Math.max(0, limit - used);
            const percent_used = Math.round((used / limit) * 100);
            const nextMonth = new Date();
            nextMonth.setDate(1);
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            return reply(requestId, {
              limit, used, remaining, percent_used,
              reset_date: nextMonth.toISOString().slice(0, 10),
            });
          } catch (e) {
            return reply(requestId, null, { code: 'FIRESTORE_ERROR', message: e.message });
          }
        }

        if (block === '_meta.listBlocks') {
          return reply(requestId, [
            {
              name: 'ai.chat',
              status: 'available',
              available_in_external: false,
              description: 'Anropa Claude med konversationsformat. Stöder multi-turn historik, system-prompts och modellval.',
              schema: {
                messages:   'Array<{ role: "user"|"assistant", content: string }> — Krävs',
                model:      'string — Valfritt, default "claude-sonnet-4-6"',
                max_tokens: 'number — Valfritt, default 1024',
                system:     'string — Valfritt, system-prompt',
              },
            },
            {
              name: 'ai.extract',
              status: 'available',
              available_in_external: false,
              description: 'Extrahera strukturerad data ur fritext med Claude. Returnerar ett JSON-objekt enligt ett schema du definierar.',
              schema: {
                text:       'string — Krävs, texten att extrahera från',
                schema:     'object — Krävs, beskriver fälten att extrahera',
                model:      'string — Valfritt, default "claude-haiku-4-5"',
                max_tokens: 'number — Valfritt, default 1024',
              },
            },
            {
              name: 'qr.generate',
              status: 'available',
              available_in_external: true,
              description: 'Generera en QR-kod som PNG (data URL). Ingen AI-kvot förbrukas.',
              schema: {
                text:              'string — Krävs, innehållet i QR-koden',
                size:              'number — Valfritt, pixelstorlek 100–1000 (default 300)',
                error_correction:  'string — Valfritt, "L"|"M"|"Q"|"H" (default "M")',
              },
            },
            {
              name: 'chart.render',
              status: 'available',
              available_in_external: true,
              description: 'Rendera ett diagram som SVG-sträng (bar, line, pie). Ingen AI-kvot förbrukas.',
              schema: {
                type:     'string — "bar"|"line"|"pie" (default "bar")',
                labels:   'string[] — Krävs, x-axelns etiketter',
                datasets: 'Array<{ label, data: number[], color? }> — Krävs',
                title:    'string — Valfritt, diagramtitel',
                width:    'number — Valfritt, px (default 600)',
                height:   'number — Valfritt, px (default 380)',
              },
            },
            {
              name: 'wordcloud.generate',
              status: 'available',
              available_in_external: true,
              description: 'Räkna ordfrekvens i text och returnera de vanligaste orden. Ingen AI-kvot förbrukas.',
              schema: {
                text:       'string — Krävs, texten att analysera',
                max_words:  'number — Valfritt, max antal ord (default 50)',
                min_length: 'number — Valfritt, min ordlängd (default 3)',
              },
            },
          ]);
        }
      }

      // ── Block-whitelist ──────────────────────────────────────────────────────
      if (!ALLOWED_BLOCKS.includes(block)) {
        return reply(requestId, null, { code: 'INVALID_BLOCK', message: `Okänt block: ${block}` });
      }

      // ── Anropa gateway ───────────────────────────────────────────────────────
      try {
        const token = await currentUser.getIdToken();
        const res   = await fetch('/.netlify/functions/applab-gateway', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ block, params }),
        });
        const data = await res.json();

        if (!res.ok) {
          return reply(requestId, null, { code: 'GATEWAY_ERROR', message: data.error || 'Gateway-fel' });
        }

        reply(requestId, data);

        // Kolla kvot och skicka varning om >80%
        getDoc(doc(db, 'applab_quotas', currentUser.uid)).then(snap => {
          const q     = snap.data() || {};
          const month = new Date().toISOString().slice(0, 7);
          const used  = q.month === month ? (q.tokens_used_this_month || 0) : 0;
          const limit = q.monthly_limit || 1_000_000;
          const pct   = Math.round((used / limit) * 100);
          if (pct >= QUOTA_WARN_PCT) {
            sendEvent('quota_warning', { percent_used: pct, used, limit });
          }
        }).catch(() => {});

      } catch (e) {
        reply(requestId, null, { code: 'NETWORK_ERROR', message: e.message });
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [active, currentUser, iframeRef]);
}
